/* Bronson quick view: modal shell, section-rendered product content, variant picker, add to cart */
(() => {
  if (window.BronsonQuickView) return;

  const SECTION_ID = 'bronson-quick-view';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const CLOSE_ICON =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m6.5 6.5 15 15M21.5 6.5l-15 15"/></svg>';
  const cache = new Map();

  const sectionUrl = (url) => {
    const u = new URL(url, window.location.origin);
    u.search = '';
    u.hash = '';
    u.searchParams.set('section_id', SECTION_ID);
    return u.toString();
  };

  const load = (url) => {
    const key = sectionUrl(url);
    if (!cache.has(key)) {
      const request = fetch(key)
        .then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then((text) => {
          const node = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-qv-product]');
          if (!node) throw new Error('Quick view content missing');
          return node.outerHTML;
        })
        .catch((err) => {
          cache.delete(key);
          throw err;
        });
      cache.set(key, request);
    }
    return cache.get(key);
  };

  const withWidth = (src, width) => {
    if (!src) return src;
    const u = new URL(src, window.location.origin);
    u.searchParams.set('width', width);
    return u.toString();
  };

  class QuickView {
    constructor() {
      this.el = document.createElement('div');
      this.el.className = 'bqv-modal';
      this.el.hidden = true;
      this.el.setAttribute('role', 'dialog');
      this.el.setAttribute('aria-modal', 'true');
      this.el.setAttribute('aria-label', 'Quick view');
      this.el.innerHTML = `
        <div class="bqv-modal__wash" data-qv-close></div>
        <div class="bqv-modal__inner">
          <div class="bqv-modal__loader" aria-hidden="true"><span></span></div>
          <div class="bqv-modal__content" data-qv-content></div>
          <button type="button" class="bqv-modal__close" data-qv-close aria-label="Close">${CLOSE_ICON}</button>
        </div>`;
      document.body.appendChild(this.el);
      this.content = this.el.querySelector('[data-qv-content]');
      this.token = 0;

      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-qv-close]')) this.close();
      });
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    async open(url, config, trigger) {
      const token = ++this.token;
      this.config = config || {};
      this.trigger = trigger;
      this.url = url;

      clearTimeout(this.hideTimer);
      this.content.innerHTML = '';
      this.el.hidden = false;
      this.el.classList.remove('is-loaded', 'is-closing');
      this.el.classList.add('is-loading');
      void this.el.offsetWidth;
      this.el.classList.add('is-open');
      document.documentElement.classList.add('bh-scroll-lock');
      this.el.querySelector('.bqv-modal__close').focus({ preventScroll: true });

      let html;
      try {
        html = await load(url);
      } catch (err) {
        if (token === this.token) window.location.href = url;
        return;
      }
      if (token !== this.token || !this.el.classList.contains('is-open')) return;

      this.content.innerHTML = html;
      this.init();
      this.el.classList.remove('is-loading');
      void this.el.offsetWidth;
      this.el.classList.add('is-loaded');
    }

    close() {
      if (this.el.hidden || !this.el.classList.contains('is-open')) return;
      this.token++;
      this.el.classList.remove('is-open');
      this.el.classList.add('is-closing');
      document.documentElement.classList.remove('bh-scroll-lock');
      this.hideTimer = setTimeout(() => {
        this.el.hidden = true;
        this.el.classList.remove('is-closing', 'is-loaded', 'is-loading');
        this.content.innerHTML = '';
      }, 350);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }

    trapFocus(e) {
      const items = [...this.el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- content ---------- */
    init() {
      const root = this.content.querySelector('[data-qv-product]');
      this.root = root;
      const cfg = this.config;
      this.data = JSON.parse(root.querySelector('[data-qv-json]').textContent);

      root.classList.toggle('bqv--dropdown', cfg.picker === 'dropdown');
      const toggle = (sel, show) => root.querySelectorAll(sel).forEach((n) => n.classList.toggle('bqv-hidden', !show));
      toggle('[data-qv-vendor]', cfg.vendor !== false);
      toggle('.bqv__sku', cfg.sku !== false);
      toggle('[data-qv-quantity-block]', cfg.quantity !== false);
      toggle('[data-qv-description]', cfg.description !== false);

      if (cfg.detailsLabel) root.querySelector('[data-qv-details-label]').textContent = cfg.detailsLabel;

      const badgeTags = String(cfg.badgeTags || '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      root.querySelectorAll('[data-badge-tag]').forEach((b) => {
        b.hidden = cfg.badges === false || !badgeTags.includes(b.dataset.badgeTag);
      });
      root.querySelectorAll('[data-badge-sold-out]').forEach((b) => (b.hidden = cfg.badges === false));

      this.options = [...root.querySelectorAll('[data-qv-option]')];
      const swatchName = String(cfg.swatchOption || '').trim().toLowerCase();
      this.selected = this.options.map((opt) => {
        if (swatchName && opt.dataset.name === swatchName && opt.querySelector('.bqv-value__swatch')) opt.classList.add('is-swatch');
        const sel = opt.querySelector('.bqv-value.is-selected') || opt.querySelector('.bqv-value');
        return sel ? sel.dataset.value : null;
      });

      // linked products (color group metafield): load the chosen product into the popup
      const group = root.querySelector('[data-qv-group]');
      if (group) {
        const groupLabel = group.querySelector('[data-qv-group-label]');
        if (groupLabel && cfg.groupLabel) groupLabel.textContent = cfg.groupLabel;
        group.querySelectorAll('[data-qv-switch]').forEach((link) =>
          link.addEventListener('click', (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            if (link.classList.contains('is-selected')) return;
            this.open(link.dataset.qvSwitch, this.config, this.trigger);
          })
        );
        // a single-value color option is redundant next to the group
        this.options.forEach((opt) => {
          if (opt.dataset.name === swatchName && opt.querySelectorAll('.bqv-value').length < 2) opt.classList.add('bqv-hidden');
        });
      }

      this.options.forEach((opt, index) => {
        opt.querySelectorAll('.bqv-value').forEach((btn) =>
          btn.addEventListener('click', () => this.select(index, btn.dataset.value))
        );
        opt.querySelector('[data-qv-select]')?.addEventListener('change', (e) => this.select(index, e.target.value));
      });

      const qty = root.querySelector('[data-qv-qty]');
      root.querySelector('[data-qv-minus]')?.addEventListener('click', () => {
        qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1);
      });
      root.querySelector('[data-qv-plus]')?.addEventListener('click', () => {
        qty.value = (parseInt(qty.value, 10) || 0) + 1;
      });
      qty?.addEventListener('change', () => {
        qty.value = Math.max(1, parseInt(qty.value, 10) || 1);
      });

      root.querySelector('[data-qv-add]')?.addEventListener('click', () => this.addToCart());
      this.update();
    }

    select(index, value) {
      this.selected[index] = value;
      this.update();
    }

    findVariant(selected) {
      return this.data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    }

    update() {
      const root = this.root;
      const variant = this.findVariant(this.selected);
      this.variant = variant;

      this.options.forEach((opt, index) => {
        const current = this.selected[index];
        opt.querySelectorAll('.bqv-value').forEach((btn) => {
          const value = btn.dataset.value;
          const isSelected = value === current;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', String(isSelected));
          const combo = [...this.selected];
          combo[index] = value;
          const match = this.findVariant(combo);
          btn.classList.toggle('is-unavailable', !match || !match.available);
        });
        const select = opt.querySelector('[data-qv-select]');
        if (select) select.value = current;
        const label = opt.querySelector('[data-qv-option-value]');
        if (label) label.textContent = current;
      });

      const price = root.querySelector('[data-qv-price]');
      const compare = root.querySelector('[data-qv-compare]');
      const skuWrap = root.querySelector('[data-qv-sku]');
      const add = root.querySelector('[data-qv-add]');
      const addText = root.querySelector('[data-qv-add-text]');
      const details = root.querySelector('[data-qv-details]');
      root.querySelector('[data-qv-error]').hidden = true;

      if (variant) {
        price.textContent = variant.price;
        compare.textContent = variant.compare || '';
        compare.hidden = !variant.compare;
        root.querySelector('[data-qv-sku-value]').textContent = variant.sku || '';
        skuWrap.hidden = !variant.sku;
        details.href = `${this.data.url}?variant=${variant.id}`;
        if (variant.image) {
          root.querySelectorAll('[data-qv-image]').forEach((img) => {
            const width = img.closest('.bqv__header-media') ? 400 : 900;
            const next = withWidth(variant.image, width);
            if (img.dataset.current === next) return;
            img.dataset.current = next;
            img.removeAttribute('srcset');
            img.src = next;
          });
        }
      }

      const available = Boolean(variant && variant.available);
      add.disabled = !available;
      addText.textContent = !variant
        ? 'Unavailable'
        : available
          ? this.config.addLabel || 'Add to Bag'
          : add.dataset.soldOut || 'Sold out';
    }

    async addToCart() {
      const root = this.root;
      const variant = this.variant;
      const add = root.querySelector('[data-qv-add]');
      const error = root.querySelector('[data-qv-error]');
      if (!variant || !variant.available || add.classList.contains('is-busy')) return;

      const quantity = Math.max(1, parseInt(root.querySelector('[data-qv-qty]')?.value, 10) || 1);
      const bronsonCart = window.BronsonCart;
      const cart = bronsonCart ? null : document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = new FormData();
      body.append('id', variant.id);
      body.append('quantity', quantity);
      if (bronsonCart) {
        body.append('sections', bronsonCart.sections.join(','));
        body.append('sections_url', window.location.pathname);
      } else if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(this.trigger || document.activeElement);
      }

      add.classList.add('is-busy');
      error.hidden = true;
      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          error.textContent = json.description || json.message || 'Something went wrong. Please try again.';
          error.hidden = false;
          return;
        }

        if (bronsonCart) {
          const trigger = this.trigger;
          this.close();
          // reveal the notification as the quick view fades out, like the reference
          setTimeout(() => bronsonCart.added(json, trigger), 300);
          return;
        }

        add.classList.add('is-added');
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bronson-quick-view', productVariantId: variant.id, cartData: json });
        }
        setTimeout(() => {
          this.close();
          if (cart && typeof cart.renderContents === 'function') cart.renderContents(json);
          else window.location.href = window.routes?.cart_url || '/cart';
        }, 700);
      } catch (e) {
        error.textContent = 'Something went wrong. Please try again.';
        error.hidden = false;
      } finally {
        add.classList.remove('is-busy');
      }
    }
  }

  let instance;
  const getInstance = () => (instance ||= new QuickView());

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger || e.defaultPrevented) return;
    const host = trigger.closest('[data-qv-config]');
    if (!host || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    let config = {};
    try {
      config = JSON.parse(host.dataset.qvConfig);
    } catch (err) {}
    getInstance().open(trigger.dataset.quickView || trigger.href, config, trigger);
  });

  // warm the cache when a shopper shows intent on desktop
  document.addEventListener(
    'pointerenter',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const trigger = e.target.closest?.('[data-quick-view]');
      if (trigger && trigger.closest('[data-qv-config]')) load(trigger.dataset.quickView || trigger.href).catch(() => {});
    },
    true
  );

  window.BronsonQuickView = { open: (url, config, trigger) => getInstance().open(url, config, trigger) };
})();
